import { useEffect, useMemo } from "react";
import create from "zustand";
import { NGROK } from "../APIs";
import { apiClient } from "../utils/apiClient";
import { myLocalStorage } from "../components/StorageHelper";

export interface CloudCredentialStatus {
  cloudProviderType: "AWS" | "AZURE" | "GCP";
  cloudAccountId: string;
  status: "VALID" | "EXPIRED";
  reason?: string | null;
  expiredAt?: string | null;
  lastCheckedAt?: string | null;
}

export interface CloudCredentialExpiry {
  expired: boolean;
  reason?: string;
  expiredAccounts: CloudCredentialStatus[];
}

export const CREDENTIALS_EXPIRED_LABEL = "Credentials expired";

const REFRESH_INTERVAL_MS = 60_000;
const MIN_FETCH_GAP_MS = 10_000;
const inFlightFetches = new Map<string, Promise<void>>();
const NO_STATUSES: CloudCredentialStatus[] = [];

interface CloudCredentialStore {
  statusesByTenant: Record<string, CloudCredentialStatus[]>;
  fetchedAt: Record<string, number>;
  fetchStatuses: (tenantName: string, force?: boolean) => Promise<void>;
}

const useCloudCredentialStore = create<CloudCredentialStore>((set, get) => ({
  statusesByTenant: {},
  fetchedAt: {},

  fetchStatuses: async (tenantName, force = false) => {
    if (!tenantName) return;
    const lastFetchedAt = get().fetchedAt[tenantName] || 0;
    if (!force && Date.now() - lastFetchedAt < MIN_FETCH_GAP_MS) return;
    const pending = inFlightFetches.get(tenantName);
    if (pending) return pending;

    const request = (async () => {
      try {
        const data = await apiClient<CloudCredentialStatus[]>({
          method: "GET",
          url: `${NGROK}/api/cloud-credentials/status`,
          params: { tenantName },
        });
        set((state) => ({
          statusesByTenant: { ...state.statusesByTenant, [tenantName]: Array.isArray(data) ? data : [] },
          fetchedAt: { ...state.fetchedAt, [tenantName]: Date.now() },
        }));
      } catch (error) {
        console.error("Failed to fetch cloud credential status:", error);
      } finally {
        inFlightFetches.delete(tenantName);
      }
    })();
    inFlightFetches.set(tenantName, request);
    return request;
  },
}));

export const formatCredentialReason = (reason?: string | null): string | undefined =>
  reason?.replace(/\s*\([A-Z]+\d+\)/g, "").trim() || undefined;

const resolveTenantName = (tenantName?: string): string => {
  if (tenantName) return tenantName;
  const stored = myLocalStorage.getItem("latestTenant");
  return stored?.tenantName || (typeof stored === "string" ? stored : "") || "";
};

export const getCloudCredentialExpiry = (
  statuses: CloudCredentialStatus[] | undefined,
  cloud: string | undefined,
  accountId?: string,
): CloudCredentialExpiry => {
  const cloudType = cloud?.toUpperCase();
  const expiredAccounts = (statuses || []).filter(
    (status) =>
      status.status === "EXPIRED" &&
      status.cloudProviderType === cloudType &&
      (cloudType !== "GCP" || !accountId || status.cloudAccountId === accountId),
  );
  return {
    expired: expiredAccounts.length > 0,
    reason: formatCredentialReason(expiredAccounts.find((status) => status.reason)?.reason),
    expiredAccounts,
  };
};

export function useCloudCredentialStatuses(tenantName?: string, enabled = true): CloudCredentialStatus[] {
  const resolvedTenant = resolveTenantName(tenantName);
  const statuses = useCloudCredentialStore((state) => state.statusesByTenant[resolvedTenant]);

  useEffect(() => {
    if (!enabled || !resolvedTenant) return;
    const { fetchStatuses } = useCloudCredentialStore.getState();
    fetchStatuses(resolvedTenant);
    const intervalId = window.setInterval(() => fetchStatuses(resolvedTenant), REFRESH_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [resolvedTenant, enabled]);

  return (enabled && statuses) || NO_STATUSES;
}

export function useCloudCredentialExpiry(
  cloud: string | undefined,
  tenantName?: string,
  accountId?: string,
  enabled = true,
): CloudCredentialExpiry {
  const statuses = useCloudCredentialStatuses(tenantName, enabled);
  return useMemo(() => getCloudCredentialExpiry(statuses, cloud, accountId), [statuses, cloud, accountId]);
}

export const refreshCloudCredentialStatuses = (tenantName?: string) =>
  useCloudCredentialStore.getState().fetchStatuses(resolveTenantName(tenantName), true);

export default useCloudCredentialStore;
